import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal } from 'react-bootstrap';
import {
  CELSIUS_ID,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelValueRender';
import ReadingType from '@avtechsoftware/ra-javascript-utilities/src/types/ReadingType';
import dashboardApi from '../../services/dashboardApi';
import { METRIC_CATEGORY, STATISTICS_METRICS_BY_KEY } from './statisticsMetrics';

const EMPTY_VALUE = '—';

export const useStatisticsWidgetData = (context, { isEditMode = false, widgetId = null } = {}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);

  const enabledKey = ((context && context.enabledStatistics) || []).join(',');

  useEffect(() => {
    if (isEditMode) return undefined;
    if (widgetId == null) {
      setData({});
      setIsLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);

    dashboardApi.statistics
      .getStatistics(widgetId)
      .then((response) => {
        if (cancelled) return;
        const body = dashboardApi.unwrapBody(response) || {};
        const payload = body.statistics || {};
        setData(payload.statistics || {});
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setData({});
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [widgetId, isEditMode, enabledKey]);

  return { isLoading, data, viewState: {} };
};

const readItems = (raw) => (raw && typeof raw === 'object' ? raw.items : null);

const readValue = (raw) => {
  if (raw != null && typeof raw === 'object') {
    return 'value' in raw ? raw.value : null;
  }
  return raw;
};

const toMoment = (value, tz) => {
  let input = value;
  if (value && typeof value === 'object' && typeof value.epochSecond === 'number') {
    input = value.epochSecond * 1000 + Math.round((value.nano || 0) / 1e6);
  }
  return tz ? moment(input).tz(tz) : moment(input);
};

const formatTempString = (raw, temperatureScale) => {
  const parts = raw.split(' - ');
  const celsius = Number(parts.pop());
  const options = { temperatureScale };
  const converted = ReadingType.TEMPERATURE.toUserDisplayValue(celsius, options);
  const units = ReadingType.TEMPERATURE.getUnits(options);

  parts.push(`${converted.toFixed(2)} ${units}`);
  return parts.join(' - ');
};

const formatValue = (metric, raw, userPreferences) => {
  if (raw == null) return EMPTY_VALUE;

  const value = readValue(raw);
  if (value == null) return EMPTY_VALUE;

  switch (metric.category) {
    case METRIC_CATEGORY.COUNT: {
      return value;
    }
    case METRIC_CATEGORY.TIMESTAMP: {
      const tz = userPreferences && userPreferences.timezone;
      const m = toMoment(value, tz);
      return m.isValid() ? m.format('lll') : EMPTY_VALUE;
    }
    case METRIC_CATEGORY.TEMP: {
      const temperatureScale = (userPreferences && userPreferences.temperatureScale)
        || CELSIUS_ID;
      if (typeof value === 'object') {
        const reading = value.reading != null
          ? formatTempString(String(value.reading), temperatureScale)
          : null;
        const parts = [value.label, value.component, reading].filter(Boolean);
        return parts.length ? parts.join(' · ') : EMPTY_VALUE;
      }
      return formatTempString(value, temperatureScale);
    }
    case METRIC_CATEGORY.TEXT:
    default:
      // Guard against any unexpected object slipping through to React.
      return typeof value === 'object' ? EMPTY_VALUE : value;
  }
};

const StatisticsWidgetView = ({ context, data, userPreferences }) => {
  const [drill, setDrill] = useState(null); // { metric, items }

  // eslint-disable-next-line react/destructuring-assignment
  const selectedKeys = (context && context.enabledStatistics) || [];
  const metrics = selectedKeys
    .map((key) => STATISTICS_METRICS_BY_KEY[key])
    .filter(Boolean);

  const rows = metrics.map((metric) => {
    const raw = data ? data[metric.key] : null;
    const items = readItems(raw);
    const isDrillable = !!(metric.drillSource && items && items.length);

    const handleDrill = () => {
      if (isDrillable) setDrill({ metric, items });
    };

    return (
      <tr key={metric.key}>
        <td className="text-nowrap">
          <FontAwesomeIcon
            icon={metric.icon}
            style={{ color: metric.color }}
            className="mr-2"
            fixedWidth
          />
          {metric.label}
        </td>
        <td className="text-right">
          {isDrillable ? (
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={handleDrill}
            >
              {formatValue(metric, raw, userPreferences)}
            </button>
          ) : (
            formatValue(metric, raw, userPreferences)
          )}
        </td>
      </tr>
    );
  });

  return (
    <div className="w-100 pt-4">
      <table className="table table-sm table-striped mb-0">
        <thead>
          <tr>
            <th scope="col" className="border-0">Metric</th>
            <th scope="col" className="border-0 text-right">State</th>
          </tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>

      <Modal show={!!drill} onHide={() => setDrill(null)} centered>
        {drill && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>
                <FontAwesomeIcon
                  icon={drill.metric.icon}
                  style={{ color: drill.metric.color }}
                  className="mr-2"
                  fixedWidth
                />
                {drill.metric.label}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <ul className="list-unstyled mb-0">
                {drill.items.map((item) => (
                  <li key={item.id != null ? item.id : item.name} className="py-1">
                    {item.name}
                  </li>
                ))}
              </ul>
            </Modal.Body>
          </>
        )}
      </Modal>
    </div>
  );
};

StatisticsWidgetView.propTypes = {
  context: PropTypes.shape({
    enabledStatistics: PropTypes.arrayOf(PropTypes.string),
  }),
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object,
  userPreferences: PropTypes.shape({
    timezone: PropTypes.string,
  }),
};

StatisticsWidgetView.defaultProps = {
  context: {},
  data: {},
  userPreferences: {},
};

export default StatisticsWidgetView;
